import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Icon } from '@/lib/icons';
import { fill, type LeaderboardCopy } from '../leaderboard-copy';
import { publicName, type NextRankGap } from '../leaderboard-ranking';
import type { LeaderboardEntry } from '../leaderboard.types';

interface YourPositionProps {
  copy: LeaderboardCopy;
  base: string;
  entry?: LeaderboardEntry;
  gap: NextRankGap | null;
  /** Contributors on the board, for "rank 12 of 100". */
  total: number;
  format: (value: number) => string;
}

/**
 * The signed-in contributor's own line: rank, count and the distance to the
 * next rank. Outside the top 100 the API gives no rank, so the panel says so.
 */
export function YourPosition({ copy, base, entry, gap, total, format }: YourPositionProps) {
  if (!entry) {
    return (
      <section
        aria-labelledby="leaderboard-position"
        className="flex flex-col gap-3 rounded-lg border border-border/60 bg-surface p-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0">
          <h2 id="leaderboard-position" className="text-[11px] font-semibold uppercase tracking-[0.08em] text-brand">
            {copy.positionTitle}
          </h2>
          <p className="mt-1 text-sm font-medium text-foreground">{copy.positionUnranked}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{fill(copy.positionUnrankedHint, { count: total })}</p>
        </div>
        <Button asChild size="sm" className="self-start sm:self-auto">
          <Link to={`${base}/speak-contribute`}>
            {copy.contribute}
            <Icon name="arrowRight" />
          </Link>
        </Button>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="leaderboard-position"
      className="grid gap-4 rounded-lg border border-brand/30 bg-brand/[0.06] p-4 sm:grid-cols-[auto_auto_minmax(0,1fr)] sm:items-center sm:gap-8"
    >
      <h2 id="leaderboard-position" className="sr-only">
        {copy.positionTitle}
      </h2>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-brand">{copy.positionRank}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
          {entry.rank}
          <span className="ml-1.5 text-sm font-normal text-muted-foreground">{fill(copy.positionOf, { count: total })}</span>
        </p>
      </div>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-brand">{copy.positionValue}</p>
        <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{format(entry.contributions)}</p>
      </div>
      <p className="text-sm text-muted-foreground sm:text-right">
        {gap
          ? fill(copy.positionGap, { needed: format(gap.needed), name: publicName(gap.above), rank: gap.above.rank })
          : copy.positionLeader}
      </p>
    </section>
  );
}
